// Joy, in transforms and opacity only. Every reward lands visually, since
// iOS has no vibration on the web. Reduced motion keeps the reward and drops
// the travel: a flash where the sparks would have flown, the final number
// with a pop, a wash of honey instead of falling confetti.
import { reducedMotion } from './viewport';

const SPARK_MS = 600, FLASH_MS = 450, CONFETTI_MS = 4200;

/** Sparks from a point in viewport pixels, as from a tick's circle. */
export function spark(x: number, y: number, count = 10): void {
  if (reducedMotion()) {
    const f = document.createElement('i');
    f.className = 'spark-flash';
    f.style.left = `${x}px`; f.style.top = `${y}px`;
    document.body.appendChild(f);
    setTimeout(() => f.remove(), FLASH_MS);
    return;
  }
  for (let i = 0; i < count; i++) {
    const s = document.createElement('i');
    s.className = i % 3 === 0 ? 'spark blue' : 'spark';
    const a = (i / count) * Math.PI * 2, d = 22 + Math.random() * 22;
    s.style.left = `${x}px`; s.style.top = `${y}px`;
    s.style.setProperty('--dx', `${(Math.cos(a) * d).toFixed(1)}px`);
    s.style.setProperty('--dy', `${(Math.sin(a) * d).toFixed(1)}px`);
    document.body.appendChild(s);
    setTimeout(() => s.remove(), SPARK_MS);
  }
}

/** Counts a number up (or down) in place, easing out. */
export function countUp(el: HTMLElement, from: number, to: number, ms = 500, format: (n: number) => string = (n) => n.toLocaleString('en-GB')): void {
  if (reducedMotion() || ms <= 0) { el.textContent = format(to); return; }
  el.textContent = format(from);
  const t0 = performance.now();
  const step = (t: number) => {
    const k = Math.min(1, (t - t0) / ms);
    el.textContent = format(Math.round(from + (to - from) * (1 - (1 - k) ** 3)));
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** A project finished: confetti over everything, or a wash of honey. */
export function confetti(): void {
  const c = document.createElement('div');
  c.setAttribute('aria-hidden', 'true');
  if (reducedMotion()) {
    c.className = 'confetti-wash';
    document.body.appendChild(c);
    setTimeout(() => c.remove(), FLASH_MS * 2);
    return;
  }
  c.className = 'confetti';
  for (let i = 0; i < 70; i++) {
    const p = document.createElement('i');
    p.style.left = `${(Math.random() * 100).toFixed(1)}%`;
    p.style.setProperty('--x', `${((Math.random() - 0.5) * 120).toFixed(0)}px`);
    p.style.setProperty('--d', `${(2 + Math.random() * 1.6).toFixed(2)}s`);
    p.style.setProperty('--delay', `${(Math.random() * 0.5).toFixed(2)}s`);
    c.appendChild(p);
  }
  document.body.appendChild(c);
  setTimeout(() => c.remove(), CONFETTI_MS);
}

/** A quick scale pulse (a blink under reduced motion); safe to call again mid-pulse. */
export function pop(el: HTMLElement): void {
  el.classList.remove('pop');
  void el.offsetWidth;
  el.classList.add('pop');
  el.addEventListener('animationend', () => el.classList.remove('pop'), { once: true });
}
